<template>
  <div class="astro-prompts-view">
    <sidebar-nav />
    <main class="main-content">
      <div class="dashboard-header">
        <h2>AstroLife Prompts</h2>
        <router-link to="/dashboard" class="back-link">
          &larr; Back to Dashboard
        </router-link>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="cosmic-loader"></div>
        <span>Loading AstroLife prompts...</span>
      </div>

      <div v-else class="table-container">
        <table class="prompts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th class="value-col">Value Preview</th>
              <th class="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="prompt in prompts" :key="prompt.id" :class="{ 'highlight-success': savedState[prompt.id] }">
              <td><strong>{{ prompt.name }}</strong></td>
              <td><code>{{ prompt.type }}</code></td>
              <td>
                <p class="prompt-preview">{{ truncateText(prompt.value) }}</p>
              </td>
              <td class="actions-cell">
                <button class="edit-btn" @click="openEditModal(prompt)">Edit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Edit Modal -->
      <div v-if="isModalOpen" class="modal-overlay" @click.self="closeEditModal">
        <div class="modal-content">
          <h3>Edit Prompt: {{ currentPrompt.name }}</h3>
          <textarea
            v-model="currentPrompt.value"
            class="prompt-textarea"
            ref="modalTextarea"
          ></textarea>
          <div class="modal-actions">
            <button class="cancel-btn" @click="closeEditModal">Cancel</button>
            <button class="save-btn" @click="savePrompt" :disabled="isSaving">
              {{ isSaving ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { initFirebase } from '../firebase';
import SidebarNav from './SidebarNav.vue';

export default {
  name: 'AstroLifePrompts',
  components: {
    SidebarNav
  },
  setup() {
    const { db } = initFirebase();
    const prompts = ref([]);
    const loading = ref(true);
    const isModalOpen = ref(false);
    const currentPrompt = ref(null);
    const originalPromptValue = ref('');
    const isSaving = ref(false);
    const savedState = ref({});
    const modalTextarea = ref(null);

    const loadPrompts = async () => {
      loading.value = true;
      try {
        const promptsRef = collection(db, 'astroLifePrompts');
        const querySnapshot = await getDocs(promptsRef);
        prompts.value = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error('Error loading AstroLife prompts:', error);
      } finally {
        loading.value = false;
      }
    };
    
    const openEditModal = async (prompt) => {
      currentPrompt.value = { ...prompt };
      originalPromptValue.value = prompt.value;
      isModalOpen.value = true;
      await nextTick();
      if (modalTextarea.value) {
          modalTextarea.value.focus();
      }
    };

    const closeEditModal = () => {
      isModalOpen.value = false;
      currentPrompt.value = null;
    };

    const savePrompt = async () => {
      if (!currentPrompt.value) return;
      isSaving.value = true;
      try {
        const promptDocRef = doc(db, 'astroLifePrompts', currentPrompt.value.id);
        await updateDoc(promptDocRef, { value: currentPrompt.value.value });

        const index = prompts.value.findIndex(p => p.id === currentPrompt.value.id);
        if (index !== -1) {
          prompts.value[index] = { ...currentPrompt.value };
        }
        
        savedState.value[currentPrompt.value.id] = true;
        setTimeout(() => {
          savedState.value[currentPrompt.value.id] = false;
        }, 2500);

        closeEditModal();
      } catch (error) {
        console.error(`Error saving prompt:`, error);
      } finally {
        isSaving.value = false;
      }
    };
    
    const truncateText = (text, length = 150) => {
      if (!text || text.length <= length) return text || 'Prompt value is not set.';
      return text.substring(0, length) + '...';
    };

    onMounted(loadPrompts);

    return {
      prompts, loading, isModalOpen, currentPrompt, isSaving, savedState, modalTextarea,
      openEditModal, closeEditModal, savePrompt, truncateText,
    };
  },
};
</script>

<style scoped>
.astro-prompts-view {
  display: flex;
  height: 100vh;
  background-color: var(--background-primary);
}

.main-content {
  flex-grow: 1;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: hidden;
}

.prompts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.prompts-header h2 {
  margin: 0; font-size: 24px; color: var(--text-primary);
}

.back-link {
  color: var(--text-secondary); text-decoration: none; font-size: 14px;
}

.table-container {
  flex-grow: 1;
  overflow-y: auto;
  background: rgba(26, 27, 46, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}

.prompts-table {
  width: 100%;
  border-collapse: collapse;
}

.prompts-table th,
.prompts-table td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  vertical-align: middle;
}

.prompts-table th {
  background: rgba(255, 255, 255, 0.05);
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 14px;
  position: sticky; top: 0; z-index: 1;
}

.prompts-table .value-col { width: 65%; }
.prompts-table .actions-col { width: 120px; text-align: center; }

.prompt-preview {
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.actions-cell { text-align: center; }

.edit-btn, .cancel-btn, .save-btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  width: 100%;
}

.edit-btn { background: rgba(255, 255, 255, 0.1); color: var(--text-primary); }
.edit-btn:hover { background: rgba(255, 255, 255, 0.2); }

.highlight-success {
  animation: highlight 2.5s ease-out;
}
@keyframes highlight {
  0%, 100% { background-color: transparent; }
  20%, 80% { background-color: rgba(39, 174, 96, 0.2); }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 15, 26, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #1A1B2E;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 800px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 90vh;
}

.modal-content h3 { margin: 0; color: var(--text-primary); }

.prompt-textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px;
  color: var(--text-primary);
  font-size: 14px;
  overflow-y: auto;
  min-height: 500px;
  flex-grow: 1;
}

.prompt-textarea:focus { outline: none; border-color: var(--primary-color); }

.modal-actions { display: flex; justify-content: flex-end; gap: 12px; }

.cancel-btn { background: transparent; border: 1px solid rgba(255, 255, 255, 0.1); color: var(--text-secondary); width: 120px; }
.cancel-btn:hover { background: rgba(255, 255, 255, 0.05); }

.save-btn { background: linear-gradient(135deg, var(--primary-color), var(--gradient-2)); color: white; width: 150px; }
.save-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3); }
.save-btn:disabled { opacity: 0.7; cursor: not-allowed; }

.loading-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  height: 100%; gap: 16px; color: var(--text-secondary);
}

.cosmic-loader {
  width: 40px; height: 40px;
  border: 3px solid rgba(108, 99, 255, 0.3);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }
</style> 